/**
 * missionReturn — "Back to where the job started" for top-level pages.
 *
 * Top-level pages (Messages, Inventory, …) have no Back button when reached from
 * the top bar (direct exploration). When something else sends the user there —
 * Ask Cannafax, Your Next Move™, a link on another page — the page shows one
 * quiet "Back to <origin>" link that unwinds real history, so the user lands on
 * exactly the conversation/view they left.
 */
import { isTopLevelPath } from '@/lib/pageLevels';
import type { NavAction, NavEntry } from '@/lib/navStack';

let directNavPending = false;

/** Called by the top bar: the next navigation is deliberate exploration. */
export function markDirectNav(): void {
  directNavPending = true;
}

export function consumeDirectNav(): boolean {
  const v = directNavPending;
  directNavPending = false;
  return v;
}

const LABELS: Record<string, string> = {
  '/desk': 'Your Desk',
  '/my-listings': 'Inventory',
  '/my-menu': 'My Menu',
  '/buy-requests': 'Buy Requests',
  '/supplier-menus': 'Supplier Menus',
  '/messages': 'Messages',
  '/watchlist': 'Following',
};

export function originLabel(pathname: string): string {
  if (LABELS[pathname]) return LABELS[pathname];
  if (pathname.startsWith('/listings/')) return 'the listing';
  if (pathname.startsWith('/menu')) return 'the menu';
  return 'previous page';
}

export interface MissionOrigin {
  pathname: string;
  label: string;
}

/**
 * Decide the return origin after a navigation. REPLACE (filter/sort writes)
 * keeps the existing origin; POP or a top-bar click clears it.
 */
export function nextMissionOrigin(
  prevOrigin: MissionOrigin | null,
  event: { action: NavAction; pathname: string; direct: boolean },
  from: NavEntry | null,
): MissionOrigin | null {
  if (!isTopLevelPath(event.pathname)) return null;
  if (event.action === 'REPLACE') return prevOrigin;
  if (event.action === 'POP' || event.direct) return null;
  if (!from || from.pathname === event.pathname) return prevOrigin;
  return { pathname: from.pathname, label: originLabel(from.pathname) };
}
